/**
 * Approximate a Munsell colour notation ("10YR 3/2", "2.5YR 4/6", "N 5/0") as
 * an sRGB hex string, for the small colour dot beside a Munsell picker.
 *
 * Munsell is a perceptual system with no closed-form sRGB formula — an exact
 * conversion needs the renotation lookup table. This is a deliberately light
 * approximation: Value → L* (ASTM D1535), Chroma → CIELAB chroma, and the
 * Munsell hue circle → a CIELAB hue angle by interpolating the ten principal
 * hues. Good enough for a UI swatch, not a colour-accurate rendering.
 */

/** Start position of each hue family on the 0–100 Munsell hue circle. */
const FAMILY_BASE: Record<string, number> = {
    R: 0,
    YR: 10,
    Y: 20,
    GY: 30,
    G: 40,
    BG: 50,
    B: 60,
    PB: 70,
    P: 80,
    RP: 90
};

/**
 * Approximate CIELAB hue angle (degrees) at each family centre (the "5" step,
 * so positions 5, 15, … 95). The last wraps past 360 to stay continuous with 5R.
 */
const HUE_ANCHORS: { pos: number; angle: number }[] = [
    { pos: 5, angle: 30 }, // 5R
    { pos: 15, angle: 55 }, // 5YR
    { pos: 25, angle: 90 }, // 5Y
    { pos: 35, angle: 120 }, // 5GY
    { pos: 45, angle: 155 }, // 5G
    { pos: 55, angle: 200 }, // 5BG
    { pos: 65, angle: 240 }, // 5B
    { pos: 75, angle: 280 }, // 5PB
    { pos: 85, angle: 330 }, // 5P
    { pos: 95, angle: 365 } // 5RP
];

/** Munsell hue-circle position (0–100) → an approximate CIELAB hue angle. */
function munsellHueToLabAngle(pos: number): number {
    const position = ((pos % 100) + 100) % 100;
    for (let i = 0; i < HUE_ANCHORS.length; i++) {
        const anchor = HUE_ANCHORS[i];
        const nextAnchor = HUE_ANCHORS[(i + 1) % HUE_ANCHORS.length];
        const lo = anchor.pos;
        let hi = nextAnchor.pos;
        let point = position;
        let bAngle = nextAnchor.angle;
        if (hi <= lo) {
            // The wrap segment (5RP → 5R): extend past 100 and past 360.
            hi += 100;
            bAngle += 360;
            if (point < lo) {
                point += 100;
            }
        }
        if (point >= lo && point <= hi) {
            const fraction = (point - lo) / (hi - lo);
            const angle = anchor.angle + fraction * (bAngle - anchor.angle);
            return ((angle % 360) + 360) % 360;
        }
    }
    return HUE_ANCHORS[0].angle;
}

/** Munsell Value (0–10) → luminance factor Y (0–100), ASTM D1535. */
function munsellValueToY(value: number): number {
    return (
        1.1914 * value -
        0.22533 * value * value +
        0.23352 * value ** 3 -
        0.020484 * value ** 4 +
        0.00081939 * value ** 5
    );
}

/** Luminance factor Y (0–100) → CIELAB L*. */
function yToLstar(y: number): number {
    const yr = Math.max(0, y) / 100;
    return yr > 0.008856 ? 116 * Math.cbrt(yr) - 16 : 903.3 * yr;
}

function clamp255(value: number): number {
    return Math.max(0, Math.min(255, Math.round(value)));
}

function gammaEncode(linear: number): number {
    const clamped = Math.max(0, Math.min(1, linear));
    return clamped <= 0.0031308
        ? 12.92 * clamped
        : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
}

/**
 * CIELAB (D65) → sRGB hex.
 *
 * The single letters below are the standard symbols of the colour spaces the
 * conversion walks through — L*a*b*, CIE XYZ, linear sRGB — kept as the
 * specification writes them, so the matrix can be checked against the source.
 */
function labToHex(L: number, a: number, b: number): string {
    const fy = (L + 16) / 116;
    const fx = fy + a / 500;
    const fz = fy - b / 200;
    const eps = 0.008856;
    const kappa = 903.3;
    const fInv = (f: number) =>
        f ** 3 > eps ? f ** 3 : (116 * f - 16) / kappa;
    // D65 reference white.
    const X = fInv(fx) * 95.047;
    const Y = (L > kappa * eps ? ((L + 16) / 116) ** 3 : L / kappa) * 100;
    const Z = fInv(fz) * 108.883;
    const x = X / 100;
    const y = Y / 100;
    const z = Z / 100;
    const r = x * 3.2406 + y * -1.5372 + z * -0.4986;
    const g = x * -0.9689 + y * 1.8758 + z * 0.0415;
    const bl = x * 0.0557 + y * -0.204 + z * 1.057;
    const toHex = (channel: number) =>
        clamp255(gammaEncode(channel) * 255)
            .toString(16)
            .padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(bl)}`;
}

const NEUTRAL_RE = /^N\s*([\d.]+)\s*\/?\s*0*(?:\.0*)?$/i;
const CHROMATIC_RE = /^([\d.]+)\s*([A-Za-z]{1,2})\s*([\d.]+)\s*\/\s*([\d.]+)$/;

/**
 * Convert a Munsell code to an approximate sRGB hex string, or null when it
 * can't be parsed (the caller then shows no swatch). Neutrals ("N 5/0") render
 * as a pure grey of the matching lightness.
 */
export function munsellToHex(code: string): string | null {
    // A German decimal comma ("7,5YR") reads as the point.
    const raw = code.trim().replace(/,/g, '.');
    if (raw === '') {
        return null;
    }

    const neutral = raw.match(NEUTRAL_RE);
    if (neutral) {
        const value = Number(neutral[1]);
        if (!Number.isFinite(value)) {
            return null;
        }
        return labToHex(yToLstar(munsellValueToY(value)), 0, 0);
    }

    const match = raw.match(CHROMATIC_RE);
    if (!match) {
        return null;
    }
    const prefix = Number(match[1]);
    const family = match[2].toUpperCase();
    const value = Number(match[3]);
    const chroma = Number(match[4]);
    const base = FAMILY_BASE[family];
    if (
        base === undefined ||
        !Number.isFinite(prefix) ||
        !Number.isFinite(value) ||
        !Number.isFinite(chroma)
    ) {
        return null;
    }

    const L = yToLstar(munsellValueToY(value));
    // ~5 CIELAB chroma units per Munsell chroma step (rough, but stable for a dot).
    const cLab = chroma * 5;
    const angle = (munsellHueToLabAngle(base + prefix) * Math.PI) / 180;
    return labToHex(L, cLab * Math.cos(angle), cLab * Math.sin(angle));
}
